/**
 * "Keep the web pages Artie used" (spec §3.3, revised): a slim card above the
 * composer when the latest finished answer used web pages, offering to keep
 * some of them as personal documents, mark the answer not useful, or dismiss
 * the card. What is kept is each PAGE's own content, fetched by the server —
 * not Artie's answer, which stays in the conversation.
 *
 * The answer is remembered per message (metadata.knowledge_prompt) so the
 * card does not come back on reload; it is also hidden locally the moment the
 * user answers, whether or not that write lands (fail-open — a convenience,
 * not data). Keeping goes through POST /core/v1/documents/from-web-pages,
 * which accepts only the STORED answer's web citations and marks it kept.
 *
 * A just-streamed answer only has a synthetic id. Its stored row is looked up
 * strictly (matchStoredAnswer — never the previous answer), retrying briefly
 * while the turn is still being saved; until it is found the dialog lists no
 * pages and nothing can be kept. The dialog lists that STORED row's pages,
 * which are exactly the ones the server will accept.
 */

import React, { useCallback, useMemo, useState } from "react";
import { Alert, Box, Button, IconButton, Link, Paper, Snackbar, Typography } from "@mui/material";
import { Globe, X } from "lucide-react";
import { Link as RouterLink } from "react-router-dom";
import type { KnowledgePromptState, Message } from "../../../chat/types";
import { TurnStatus } from "../../../chat/hooks/useStreamingTurn";
import { CHAT_COLUMN_GUTTER, CHAT_COLUMN_MAX_WIDTH } from "../layout";
import { useChatContext } from "../providers/ChatProvider";
import { KeepWebPagesDialog } from "./KeepWebPagesDialog";
import {
  findWebAnswerToKeep,
  isPersistedId,
  matchStoredAnswer,
  pagesLabel,
  webSourcesOf,
  type WebAnswerToKeep,
} from "./webAnswer";
import type { KeptWebPage } from "../../../api/documentsApi";

const IN_FLIGHT = new Set([TurnStatus.Connecting, TurnStatus.Preparing, TurnStatus.Streaming]);
const DOCUMENTS_PATH = "/workspace/knowledge";
const NOT_SAVED_YET = "This answer is still being saved. Try again in a moment.";

// Waits before each extra history fetch while a just-streamed answer is
// still being persisted (finalize runs as the stream closes).
const DEFAULT_RETRY_DELAYS_MS = [800, 2000];

interface DialogState {
  target: WebAnswerToKeep;
  /** The answer's stored row — its pages are what can be kept. Null while
   *  resolving. */
  stored: Message | null;
  /** Stored citation URLs the caller already keeps. */
  keptUrls: ReadonlySet<string>;
  saving: boolean;
  error: string | null;
}

const NO_URLS: ReadonlySet<string> = new Set();

/** The confirmation after a keep: new pages are fetched in the background
 *  and show up on the Knowledge page when ready. */
export function keptMessage(documents: KeptWebPage[]): string {
  const keeping = documents.filter((d) => d.status === "processing").length;
  const duplicates = documents.length - keeping;
  if (keeping === 0) {
    return duplicates === 1
      ? "This page is already in your knowledge base."
      : "These pages are already in your knowledge base.";
  }
  let text = `Keeping ${pagesLabel(keeping)} — ${keeping === 1 ? "it'll" : "they'll"} appear on the Knowledge page when ready.`;
  if (duplicates > 0) {
    text += ` ${pagesLabel(duplicates)} ${duplicates === 1 ? "was" : "were"} already in your knowledge base.`;
  }
  return text;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export interface KeepWebAnswerProps {
  /** Overridable for tests. */
  retryDelaysMs?: number[];
}

export function KeepWebAnswer({ retryDelaysMs = DEFAULT_RETRY_DELAYS_MS }: KeepWebAnswerProps = {}) {
  const { chat, client, documentsApi, sessionId, features } = useChatContext();
  const [answered, setAnswered] = useState<ReadonlySet<string>>(() => new Set());
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const target = useMemo(() => findWebAnswerToKeep(chat.history), [chat.history]);

  const hide = useCallback((...ids: string[]) => {
    setAnswered((prev) => new Set([...prev, ...ids]));
  }, []);

  /** The answer's stored row: itself when persisted, else the matching row
   *  of the stored history once it appears (null if it never does). */
  const resolveStored = useCallback(
    async (message: Message): Promise<Message | null> => {
      if (isPersistedId(message.id)) return message;
      if (!sessionId) return null;
      for (let attempt = 0; attempt <= retryDelaysMs.length; attempt++) {
        if (attempt > 0) await sleep(retryDelaysMs[attempt - 1]);
        const stored = await client.getMessages(sessionId, { limit: 20 });
        const match = matchStoredAnswer(message, stored);
        if (match) return match;
      }
      return null;
    },
    [client, sessionId, retryDelaysMs],
  );

  const record = useCallback(
    async (message: Message, state: KnowledgePromptState) => {
      hide(message.id);
      try {
        const stored = await resolveStored(message);
        if (!stored || !sessionId) {
          console.warn("keep-web-answer: answer not stored yet; card hidden locally only");
          return;
        }
        const id = stored.id;
        hide(id);
        if (state === "not_useful") void chat.submitFeedback(id, "down");
        await client.setKnowledgePrompt(sessionId, id, state);
      } catch (exc) {
        // Fail-open: the card stays hidden for this view; a reload may show it.
        console.warn("keep-web-answer: recording the card's answer failed", exc);
      }
    },
    [chat, client, hide, resolveStored, sessionId],
  );

  const openDialog = useCallback(
    (snapshot: WebAnswerToKeep) => {
      const persisted = isPersistedId(snapshot.message.id) ? snapshot.message : null;
      setDialog({ target: snapshot, stored: persisted, keptUrls: NO_URLS, saving: false, error: null });
      const settle = (patch: Partial<DialogState>) =>
        setDialog((d) => (d && d.target === snapshot ? { ...d, ...patch } : d));
      // Which pages are already kept. Fail-open: without the answer, every
      // page stays tickable and the server still answers "duplicate".
      const loadKept = (stored: Message) => {
        if (!sessionId) return;
        documentsApi
          .keptWebPages(sessionId, stored.id)
          .then((kept) => settle({ keptUrls: new Set(Object.keys(kept)) }))
          .catch((exc: unknown) => console.warn("keep-web-pages: kept-pages check failed", exc));
      };
      if (persisted) {
        loadKept(persisted);
        return;
      }
      resolveStored(snapshot.message)
        .then((stored) => {
          settle({ stored, error: stored ? null : NOT_SAVED_YET });
          if (stored) loadKept(stored);
        })
        .catch((exc: unknown) =>
          settle({ error: exc instanceof Error ? exc.message : NOT_SAVED_YET }),
        );
    },
    [documentsApi, resolveStored, sessionId],
  );

  const keep = useCallback(
    async (urls: string[]) => {
      if (!dialog?.stored || !sessionId || urls.length === 0) return;
      const { target: snapshot } = dialog;
      const messageId = dialog.stored.id;
      setDialog({ ...dialog, saving: true, error: null });
      try {
        const res = await documentsApi.keepWebPages({
          session_id: sessionId,
          message_id: messageId,
          urls,
        });
        hide(snapshot.message.id, messageId);
        setDialog(null);
        setSaved(keptMessage(res.documents));
      } catch (exc) {
        setDialog((d) =>
          d
            ? {
                ...d,
                saving: false,
                error: exc instanceof Error ? exc.message : "Couldn't keep these pages.",
              }
            : d,
        );
      }
    },
    [dialog, documentsApi, hide, sessionId],
  );

  const webPageCount = target ? webSourcesOf(target.message).length : 0;
  const showCard =
    features.keepWebAnswers &&
    !!sessionId &&
    !!target &&
    !answered.has(target.message.id) &&
    !IN_FLIGHT.has(chat.turn.status);

  return (
    <>
      {showCard && target && (
        <Box sx={{ width: "100%", maxWidth: CHAT_COLUMN_MAX_WIDTH, mx: "auto", px: CHAT_COLUMN_GUTTER, pb: 1 }}>
          <Paper
            variant="outlined"
            role="region"
            aria-label="Keep web pages"
            data-ph-scope="keep_web"
            sx={{
              px: 1.5,
              py: 0.75,
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              borderRadius: 2,
            }}
          >
            <Globe size={16} aria-hidden style={{ flexShrink: 0, opacity: 0.7 }} />
            <Typography variant="body2" sx={{ flex: 1, minWidth: 180 }}>
              Artie used {webPageCount === 1 ? "1 web page" : `${webPageCount} web pages`} for this
              answer. Any worth keeping?
            </Typography>
            <Button size="small" variant="contained" data-ph="open" onClick={() => openDialog(target)}>
              Keep pages…
            </Button>
            <Button size="small" color="inherit" data-ph="reject" onClick={() => void record(target.message, "not_useful")}>
              Not useful
            </Button>
            <IconButton
              size="small"
              aria-label="Dismiss"
              data-ph="close"
              onClick={() => void record(target.message, "dismissed")}
            >
              <X size={14} />
            </IconButton>
          </Paper>
        </Box>
      )}
      {dialog && (
        <KeepWebPagesDialog
          open
          pages={dialog.stored ? webSourcesOf(dialog.stored) : []}
          keptUrls={dialog.keptUrls}
          preparing={!dialog.stored && !dialog.error}
          saving={dialog.saving}
          error={dialog.error}
          onKeep={(urls) => void keep(urls)}
          onClose={() => setDialog(null)}
        />
      )}
      <Snackbar
        open={!!saved}
        autoHideDuration={8000}
        onClose={() => setSaved(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          onClose={() => setSaved(null)}
          action={
            <Link component={RouterLink} to={DOCUMENTS_PATH} data-ph="keep_web-knowledge" color="inherit" sx={{ alignSelf: "center" }}>
              Open Knowledge
            </Link>
          }
        >
          {saved}
        </Alert>
      </Snackbar>
    </>
  );
}
